import { AnimatePresence, motion } from 'framer-motion';
import { Check, LucideIcon, X } from 'lucide-react';
import React, { useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { ThemeColors } from '../../../shared/hooks/useCustomerInfo';

interface BaseInputProps {
  label: string;
  name: keyof any;
  placeholder: string;
  value: string;
  error: string;
  touched: boolean;
  required?: boolean;
  icon: LucideIcon;
  onBlur: () => void;
  colors: ThemeColors;
  showValidation?: boolean;
}

interface TextInputProps extends BaseInputProps {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: 'text' | 'email';
}

interface PhoneInputProps extends Omit<BaseInputProps, 'onChange'> {
  onChange: (value: string) => void;
}

// Common styling function - now considers active state for real-time validation
const getInputStyles = (touched: boolean, error: string, value: string, colors: ThemeColors, showValidation: boolean = true, isActive: boolean = false) => {

  // Base styles
  const styles = {
    width: '100%',
    backgroundColor: colors.inputBackground,
    color: colors.inputText,
    borderWidth: '0',
    borderStyle: 'none',
    borderRadius: '0.75rem',
    padding: '0.75rem 1rem 0.75rem 2.5rem',
    transition: 'all 200ms',
  };

  // Show validation when:
  // 1. The field has been touched by blur OR
  // 2. The field has a value OR
  // 3. The field is currently active (being typed in)
  const shouldShowValidation = (touched || value.length > 0 || isActive) && showValidation;

  // Dynamic box-shadow color based on validation state
  if (shouldShowValidation) {
    //@ts-expect-error
    styles.boxShadow = error
      ? `0 0 0 0.4px #EF4444` // Red for error
      : `0 0 0 0.4px ${colors.validationIconColor}`; // Green for success
  } else {
    //@ts-expect-error
    styles.boxShadow = `0 0 0 0.4px ${colors.inputBorder}`; // Normal state
  }

  return styles;
};

// Common container for error animation and validation icon
const ValidationContainer: React.FC<{
  touched: boolean;
  error: string;
  value: string;
  colors: ThemeColors;
  showValidation?: boolean;
  isActive?: boolean;
  children?: React.ReactNode;
}> = ({ touched, error, value, colors, showValidation = true, isActive = false, children }) => {
  const shouldShowValidation = (touched || value.length > 0 || isActive) && showValidation;

  return (
    <>
      <AnimatePresence>
        {shouldShowValidation && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            style={{
              position: 'absolute',
              right: '12px',
              top: '15px', // Fixed pixel value - adjust as needed
            }}
          >
            {error ? (
              <X className="lp-text-red-500" size={20} />
            ) : value.length > 0 ? (
              <Check style={{ color: colors.validationIconColor }} size={20} />
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {error && shouldShowValidation && (
          <motion.p
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.2 }}
            className="lp-text-red-400 lp-text-sm"
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>
      {children}
    </>
  );
};

// Text Input Component with active state tracking
export const TextFormInput: React.FC<TextInputProps> = ({
  label,
  name,
  placeholder,
  value,
  error,
  touched,
  required = false,
  icon: Icon,
  onChange,
  onBlur,
  type = 'text',
  colors,
  showValidation = true
}) => {
  // Add state to track if the input is currently active/focused
  const [isActive, setIsActive] = useState(false);

  // Calculate validation state dynamically
  const shouldShowValidation = (touched || value.length > 0 || isActive) && showValidation;

  // Handlers for focus and blur
  const handleFocus = () => {
    setIsActive(true);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsActive(false);
    onBlur();
  };

  // Force update on validation state change
  const [forceRender, setForceRender] = useState(0);
  React.useEffect(() => {
    setForceRender(prev => prev + 1);
  }, [shouldShowValidation, error]);

  // Determine icon position based on validation state
  const iconPositionClass = shouldShowValidation && error
    ? 'lp-top-1/3'
    : 'lp-top-1/2';

  // Determine validation color based on state
  const borderColor = shouldShowValidation
  ? (error
      ? '#EF4444'
      : value.length > 0
        ? colors.validationIconColor
        : colors.inputBorder)
  : colors.inputBorder;

  return (
    <div className="lp-h-[90px]">
      <label
        className="lp-block lp-text-sm lp-font-medium lp-mb-2"
        style={{ color: colors.titleText }}
      >
        {label} {required && <span style={{ color: colors.requiredFieldMark }}>*</span>}
      </label>
      <div className="lp-relative">
      <Icon
  style={{
    position: 'absolute',
    left: '12px',
    top: '15px', // Fixed pixel value - adjust as needed to match your design
    color: colors.iconColor
  }}
  size={20}
/>
        <div
          className="lp-w-full lp-rounded-xl lp-overflow-hidden"
          style={{
            boxShadow: `0 0 0 0.7px ${borderColor}`,
            transition: 'all 200ms'
          }}
          data-force-render={forceRender}
          data-validation-state={shouldShowValidation ? (error ? 'error' : 'success') : 'none'}
        >
          <input
            type={type}
            name={name as string}
            placeholder={placeholder}
            style={{
              width: '100%',
              backgroundColor: colors.inputBackground,
              color: colors.inputText,
              borderWidth: '0',
              borderStyle: 'none',
              borderRadius: '0.75rem',
              padding: '0.75rem 1rem 0.75rem 2.5rem',
            }}
            className="focus:lp-outline-none focus:lp-ring-0 focus:lp-ring-offset-0 focus:lp-shadow-none"
            value={value}
            onChange={onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>
        <ValidationContainer
          touched={touched}
          error={error}
          value={value}
          colors={colors}
          showValidation={showValidation}
          isActive={isActive}
        />
      </div>
    </div>
  );
};
// Phone Input Component with active state tracking
export const PhoneFormInput: React.FC<PhoneInputProps> = ({
  label,
  name,
  placeholder,
  value,
  error,
  touched,
  required = false,
  icon: Icon,
  onChange,
  onBlur,
  colors,
  showValidation = true
}) => {
  // Add state to track if the input is currently active/focused
  const [isActive, setIsActive] = useState(false);

  // Handlers for focus and blur
  const handleFocus = () => {
    setIsActive(true);
  };

  const handleBlur = () => {
    setIsActive(false);
    onBlur();
  };

  // Calculate validation state dynamically on EACH render
  const shouldShowValidation = (touched || value.length > 0 || isActive) && showValidation;

  // Get box-shadow style dynamically on EACH render
  const getContainerBoxShadow = () => {
    if (shouldShowValidation) {
      if (error) {
        return `0 0 0 0.7px #EF4444`; // Red for error
      } else if (value.length > 0) {
        return `0 0 0 0.7px ${colors.validationIconColor}`; // Green for success, only if there's a value
      } else {
        return `0 0 0 0.7px ${colors.inputBorder}`; // Normal border for empty fields
      }
    }
    return `0 0 0 0.7px ${colors.inputBorder}`; // Normal state
  };

  // Determine icon position based on validation state
  const iconPositionClass = shouldShowValidation && error
    ? 'lp-top-1/3'
    : 'lp-top-1/2';

  return (
    <div className="lp-h-[90px]">
      <label
        className="lp-block lp-text-sm lp-font-medium lp-mb-2"
        style={{ color: colors.titleText }}
      >
        {label} {required && <span style={{ color: colors.requiredFieldMark }}>*</span>}
      </label>
      <div className="lp-relative">
        <PhoneInput
          country={'us'}
          enableSearch={true}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          inputStyle={{
            width: '100%',
            height: '50px',
            backgroundColor: 'transparent',
            color: colors.inputText,
            border: 'none',
            borderRadius: '0.75rem',
            padding: '0.75rem',
            paddingLeft: '3rem',
            paddingRight: '2.5rem'
          }}
          buttonStyle={{
            backgroundColor: 'transparent',
            border: 'none',
            borderRadius: '0.75rem 0 0 0.75rem',
            height: '50px',
            width: '50px',
            left: '1px',
            top: '1px'
          }}
          containerStyle={{
            borderRadius: '0.75rem',
            border: 'none',
            boxShadow: getContainerBoxShadow(), // Calculated on each render
            transition: 'all 200ms',
            backgroundColor: colors.inputBackground
          }}
          dropdownStyle={{
            backgroundColor: colors.inputBackground,
            border: `1px solid ${colors.inputBorder}`,
            borderRadius: '0.75rem',
            marginTop: '0.25rem',
            color: colors.inputText
          }}

          searchStyle={{
            color: colors.inputText,
            backgroundColor: colors.inputBackground,
            border: `1px solid ${colors.inputBorder}`,
            borderRadius: '0.75rem 0.75rem 0 0'
          }}
          countryCodeEditable={false}
          placeholder={placeholder}
        />
        <ValidationContainer
          touched={touched}
          error={error}
          value={value}
          colors={colors}
          showValidation={showValidation}
          isActive={isActive}
        />
      </div>
    </div>
  );
};

// Phone Input styles
export const PhoneInputStyles = ({ colors }: { colors: ThemeColors }) => {
  const hexToRgba = (hex: string, alpha: number = 1) => {
    // Convert shorthand hex (#fff) to full form (#ffffff)
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);

    // Extract RGB values
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
    if (!result) return `rgba(0, 0, 0, ${alpha})`;

    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Extract the base color for styling (using titleText color)
  const baseColor = colors.titleText;
  const backgroundColor = colors.inputBackground;

  return (
    <style>
      {`
        .react-tel-input .country-list .country:hover {
          background-color: ${hexToRgba(baseColor, 0.1)} !important;
        }
        .react-tel-input .country-list .country.highlight {
          background-color: ${hexToRgba(baseColor, 0.2)} !important;
        }
        .react-tel-input .country-list::-webkit-scrollbar {
          width: 8px;
        }
        .react-tel-input .country-list::-webkit-scrollbar-track {
          background: ${backgroundColor};
        }
        .react-tel-input .country-list::-webkit-scrollbar-thumb {
          background-color: ${hexToRgba(baseColor, 0.2)};
          border-radius: 4px;
          border: 2px solid ${backgroundColor};
        }
        .react-tel-input .country-list::-webkit-scrollbar-thumb:hover {
          background-color: ${hexToRgba(baseColor, 0.3)};
        }
        .react-tel-input .country-list .search {
          background-color: ${backgroundColor};
        }
        .react-tel-input .flag-dropdown.open .selected-flag {
            background-color: ${backgroundColor} !important;
          }
      `}
    </style>
  );
};
